import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { fetchPafOverview } from '@/services/anatel';
import type { RelatorOutcomes } from '@/services/types';

export function AnatelPafOverviewPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'paf', 'overview'],
    queryFn: fetchPafOverview,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) return <div className="space-y-4 p-6"><SkeletonLoader rows={8} /></div>;
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:paf.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:paf.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:paf.explainer" />

      <FieldGuide i18nKey="anatel:paf.guide" level={2} />

      {/* The separation warning is the first thing on the page, not a footnote.
          A reader arriving from the sanction screens will otherwise read these
          amounts as fines. */}
      <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
        <p>{t('anatel:paf.separateCorpus')}</p>
      </div>

      <AsOfStrip asOf={data.as_of} />

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t('anatel:paf.cases')} value={number.format(data.corpus.cases)} />
        <Stat
          label={t('anatel:paf.finalDecisions')}
          value={number.format(data.corpus.final_decisions)}
        />
        <Stat
          label={t('anatel:paf.classified')}
          value={percent.format(data.corpus.classified / data.corpus.decisions)}
          hint={
            data.pado_classified_share != null
              ? t('anatel:paf.vsPado', {
                  share: percent.format(data.pado_classified_share),
                })
              : undefined
          }
        />
        <Stat label={t('anatel:paf.entities')} value={number.format(data.corpus.entities)} />
      </dl>

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={data.outcome_mix}
          titleKey="anatel:paf.outcomeMixTitle"
          helpKey="anatel:paf.outcomeMixHelp"
        />

        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('anatel:paf.creditTitle')}
          </h2>
          <Answer answer={data.credit_distribution} help={t('anatel:paf.creditHelp')}>
            {(summary) => (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
                {Object.entries(summary.quantiles).map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-muted-foreground">{label}</dt>
                    <dd className="tabular-nums text-foreground">{currency.format(value)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Answer>
        </section>
      </div>

      <section className="rounded-lg border bg-card p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground">
            {t('anatel:paf.relatorsTitle')}
          </h2>
          <ExportButton path="/api/v1/anatel/export/paf/relators" />
        </div>
        <Answer answer={data.relators} help={t('anatel:paf.relatorsHelp')}>
          {(rows) => (
            <div className="space-y-2">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="py-1 font-medium">{t('anatel:paf.relator')}</th>
                      <th className="py-1 text-right font-medium">{t('anatel:paf.decisions')}</th>
                      <th className="py-1 text-right font-medium">
                        {t('anatel:paf.classifiedColumn')}
                      </th>
                      <th className="py-1 font-medium">{t('anatel:paf.observedOutcomes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <RelatorRow key={row.relator} row={row} />
                    ))}
                  </tbody>
                </table>
              </div>
              {/* The floor is stated on screen rather than only in the answer's
                  notes, which `Answer` deliberately does not render. Without it
                  the seven rows showing no rate look broken rather than
                  deliberately withheld. */}
              <p className="text-xs text-muted-foreground">
                {t('anatel:paf.floorNote', {
                  shown: rows.filter((row) => row.eligible).length,
                  total: rows.length,
                  threshold: MIN_CLASSIFIED_FOR_RATE,
                })}
              </p>
            </div>
          )}
        </Answer>
      </section>

      <Link
        to="/anatel/paf/decisions"
        className="inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
      >
        {t('anatel:paf.openDecisions')}
      </Link>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</dd>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}


/**
 * The floor the backend applies before it will compute a per-relator rate.
 *
 * Mirrored here only to render the sentence that explains the withheld rows;
 * `eligible` on each row is the authority, so the table can never disagree with
 * the analytics layer about who is thin even if this number drifts.
 */
const MIN_CLASSIFIED_FOR_RATE = 15;

/** How many outcome chips a row shows before it says how many it held back. */
const CHIPS_SHOWN = 4;

function RelatorRow({ row }: { row: RelatorOutcomes }) {
  const { t, i18n } = useTranslation(['anatel']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const hidden = Math.max(row.shares.length - CHIPS_SHOWN, 0);

  return (
    <tr className="border-t align-top" data-eligible={String(row.eligible)}>
      <td className="py-1.5 pr-3 text-foreground">
        <Link
          to={`/anatel/r/${row.slug}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          {row.relator}
        </Link>
      </td>
      <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
        {number.format(row.decisions)}
      </td>
      <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
        {number.format(row.classified)}
      </td>
      <td className="py-1.5">
        {row.eligible ? (
          <ul className="flex flex-wrap gap-1">
            {row.shares.slice(0, CHIPS_SHOWN).map((share) => (
              <li
                key={share.code}
                className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
              >
                {t(`anatel:paf.outcomes.${share.code}`, { defaultValue: share.code })}{' '}
                {percent.format(share.share)}
              </li>
            ))}
            {/* The old code sliced to four and said nothing. On a multi-label
                list that is an undisclosed truncation of exactly the kind this
                product reports everywhere else. */}
            {hidden ? (
              <li className="rounded px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {t('anatel:paf.andMore', { count: hidden })}
              </li>
            ) : null}
          </ul>
        ) : (
          <span className="text-[11px] text-muted-foreground">
            {t('anatel:paf.mixWithheld')}
          </span>
        )}
      </td>
    </tr>
  );
}
